/**
 * ReactionPalette (#989)
 *
 * The fixed 12-emoji reaction palette rendered as a row of tappable swatches.
 * Shared by the two web "add a reaction" entry points: the hover quick-picker
 * over a bubble and the palette row at the top of `MessageActionSheet`.
 *
 * Rule 20 reaction-content carve-out: these palette glyphs are the picker
 * surface the carve-out explicitly covers. The palette is server-enforced (Zod
 * union in `rooms.ts`); this list mirrors `REACTION_PALETTE` from the shared
 * data layer so a palette change is a one-line edit there.
 */

import { REACTION_PALETTE, type ReactionEmoji } from '../lib/api/messages';

interface ReactionPaletteProps {
  onSelect: (emoji: ReactionEmoji) => void;
  /** Emojis the viewer has already reacted with — rendered with the accent
   *  highlight so re-tapping (which toggles OFF) reads as a de-select. */
  mine?: ReadonlySet<ReactionEmoji>;
  /** Disable EVERY swatch (whole-surface gate). */
  disabled?: boolean;
  /**
   * Per-emoji gate (#2873 item 1, Frank r1): disable ONLY the swatches whose own
   * toggle is in flight. A single `disabled` boolean driven by the mutation's
   * `isPending` freezes all 12 swatches while one unrelated emoji is settling —
   * the same row-wide freeze this issue removed from the chip row. Composed with
   * `disabled` (either one disables a swatch).
   */
  isEmojiDisabled?: (emoji: ReactionEmoji) => boolean;
  /**
   * How the swatches lay out (#3056):
   *  - `'wrap'` (default): a `flex flex-wrap` row that fills its container.
   *    Correct for the full-width action-sheet palette row.
   *  - `'grid'`: a fixed 6-column grid. The 6 non-collapsing columns give the
   *    palette an intrinsic, bounded width (2 rows of 6, ~236px), so an
   *    absolutely-positioned popover sizes to the grid instead of shrink-
   *    wrapping to its trigger chip and wrapping to a one-emoji-wide strip.
   */
  layout?: 'wrap' | 'grid';
}

export default function ReactionPalette({
  onSelect,
  mine,
  disabled,
  isEmojiDisabled,
  layout = 'wrap',
}: ReactionPaletteProps) {
  return (
    <div
      role="group"
      aria-label="Add a reaction"
      data-testid="reaction-palette"
      className={
        layout === 'grid'
          ? // `grid-cols-6` is tuned to the 12-emoji REACTION_PALETTE (2 rows of
            // 6). If that array's length changes, revisit the column count.
            'grid grid-cols-6 gap-1'
          : 'flex flex-wrap items-center gap-1'
      }
    >
      {REACTION_PALETTE.map((emoji) => {
        const isMine = mine?.has(emoji) ?? false;
        return (
          <button
            key={emoji}
            type="button"
            data-testid={`reaction-palette-${emoji}`}
            aria-label={`React with ${emoji}`}
            aria-pressed={isMine}
            disabled={(disabled ?? false) || (isEmojiDisabled?.(emoji) ?? false)}
            onClick={() => onSelect(emoji)}
            className={`flex h-9 w-9 items-center justify-center rounded-full text-lg leading-none transition-colors disabled:opacity-60 ${
              isMine ? 'bg-accent/10 ring-1 ring-accent' : 'hover:bg-oro-surface-alt'
            }`}
          >
            {emoji}
          </button>
        );
      })}
    </div>
  );
}
